/**
 * Formatting utilities for displaying data in the UI
 */

/**
 * Format amount as a simple number string (no currency symbol)
 * Always returns absolute value with 2 decimal places
 *
 * @example
 * formatAmount(-42.5) // "42.50"
 * formatAmount(100) // "100.00"
 */
export function formatAmount(amount: number): string {
  return Math.abs(amount).toFixed(2);
}

/**
 * Format amount with safe handling of undefined/null
 * Returns formatted amount with currency symbol
 *
 * @example
 * formatAmountSafe(-42.5) // "$42.50"
 * formatAmountSafe(undefined) // "$0.00"
 * formatAmountSafe(null) // "$0.00"
 */
export function formatAmountSafe(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return '$0.00';
  }
  return `$${Math.abs(amount).toFixed(2)}`;
}

/**
 * Format date in short format (e.g., "Jan 15, 2024")
 *
 * @example
 * formatDate("2024-01-15") // "Jan 15, 2024"
 * formatDate(new Date("2024-01-15")) // "Jan 15, 2024"
 */
export function formatDate(dateStr: string | Date): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Format date in MM/DD/YY format
 *
 * @example
 * formatDateShort("2024-01-15") // "01/15/24"
 */
export function formatDateShort(dateStr: string | Date): string {
  const date = new Date(dateStr);
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
}

/**
 * Format date with safe handling of undefined/null
 *
 * @example
 * formatDateSafe("2024-01-15") // "Jan 15, 2024"
 * formatDateSafe(null) // ""
 * formatDateSafe(undefined) // ""
 */
export function formatDateSafe(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '';
  return formatDate(dateStr);
}

/**
 * Re-export currency formatter from analytics for convenience
 *
 * @example
 * formatCurrency(42.5) // "$42.50"
 */
export { formatCurrency } from './analytics';
